import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { submitJoin, verifyInvitation } from "../../api/onboarding";
import { useApiError } from "../../hooks/useApiError";
import { LEGAL_VERSION } from "../legal/content";

export const useJoinFlow = (initialToken) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [token, setToken] = useState(initialToken || "");
  const [invite, setInvite] = useState(null);
  const [frames, setFrames] = useState([]);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const verify = useCallback(
    async (value) => {
      const code = (value || token).trim();
      if (!code) return;
      setBusy(true);
      setError("");
      try {
        setInvite(await verifyInvitation(code));
        setToken(code);
      } catch (err) {
        const key = err?.response?.data?.detail?.code;
        setError(key ? t(`join.${key}`) : describe(err));
        setInvite(null);
      } finally {
        setBusy(false);
      }
    },
    [token, t, describe]
  );

  const submit = async (form) => {
    if (frames.length < 1) {
      setError(t("join.step2"));
      return;
    }
    const { consents = {}, ...profile } = form;
    setBusy(true);
    setError("");
    try {
      await submitJoin(token, {
        ...profile,
        frames,
        accepted_terms: Boolean(consents.terms),
        accepted_privacy: Boolean(consents.privacy),
        accepted_eula: Boolean(consents.eula),
        accepted_biometric: Boolean(consents.biometric),
        legal_version: LEGAL_VERSION,
      });
      setDone(true);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  return { token, setToken, invite, verify, frames, setFrames, error, done, busy, submit };
};
